import type { StoreResponse } from '@clarity/contract';

/**
 * Shared store-lifecycle helpers (clarity-navigation-scope Req 2.2, 5.1).
 *
 * A branch's lifecycle is read off the two signals the store read already
 * exposes — the lifecycle `state` (`assigned | enabled | disabled | removed`)
 * and the `onboardingComplete` gate. These helpers are the single source of
 * truth for "where is this branch in its lifecycle", consumed by the scope
 * context, the scope switcher's lifecycle chips, and the onboarding lockdown.
 */

/** The four lifecycle buckets a branch can present as (Req 2.2). */
export type Lifecycle = 'live' | 'onboarding' | 'disabled' | 'removed';

/**
 * A branch is NOT-YET-ONBOARDED while it is `assigned` and onboarding is not
 * complete (Req 5.1 — unchanged from today's `isNotYetOnboarded`). This is the
 * gating signal the onboarding lockdown and the branch-only surfaces read.
 */
export function isNotYetOnboarded(store: StoreResponse): boolean {
  return store.state === 'assigned' && !store.onboardingComplete;
}

/**
 * Map a store onto its lifecycle chip (design §6):
 *
 *   removed                                    → 'removed'
 *   state === 'assigned' && !onboardingComplete → 'onboarding'
 *   state === 'disabled'                        → 'disabled'
 *   else                                        → 'live'
 *
 * `removed` is a first-class lifecycle `state`, so it is checked directly (the
 * `removedAt` / `dataDeleted` fields track the grace window + purge, but `state`
 * is the authoritative lifecycle discriminator — the `core` machine is its sole
 * writer).
 */
export function lifecycleOf(store: StoreResponse): Lifecycle {
  if (store.state === 'removed') return 'removed';
  if (isNotYetOnboarded(store)) return 'onboarding';
  if (store.state === 'disabled') return 'disabled';
  return 'live';
}
